import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import { getLocales } from 'assets/locales/Locale';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import ValueDisplay from 'assets/templates/valueDisplay/ValueDisplay';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { formatPercentage, roundToStep } from 'assets/utils/data/Number';
import { isEmpty, pick, values } from 'lodash';
import ContractRefundStatusCode from 'models/enums/ContractRefundStatusCode';
import PurchaseState from 'models/enums/PurchaseState';
import Contract from 'models/sales/contract/Model.Contract';
import ContractRefundPriceSummariesTable from '../../components/ContractRefundPriceSummariesTable';
import { RefundDistributionTable, RefundSubfeeTable } from '../../components/RefundDistributionTable';
import RefundInfoTable, { getTermStartEffectiveDate } from '../../components/RefundInfoTable';
import { useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';

const SalesContractRefundInfoGeneralInfo = () => {
  const { lang, translate } = getLocales();
  const { selected, contract, vehicle, contractCancellation } = useContractRefundProvider();

  if (!selected) return null;

  const termStartEffectiveDate = getTermStartEffectiveDate(
    selected?.refundConfigTemplateJson ?? selected?.refundConfigTemplate,
    contract
  );

  return (
    <>
      {selected.statusCode === ContractRefundStatusCode.CANCELLED && (
        <>
          <ObjectInfo
            object={{
              cancellationNote: selected.cancellationNote,
              cancelledBy: selected.cancelledBy?.displayInfo?.title,
              cancelledAtUtc: selected.cancelledAtUtc,
            }}
            objectName={lang.cancellationInfo}
          />
          &nbsp;
        </>
      )}
      <ObjectInfo
        object={pick(
          { ...contract, totalPrice: new Contract(contract).salePriceWithTaxCAD },
          'id',
          'appNumber',
          'effectiveDate',
          'paidOnLocalDate',
          'expiryDate',
          'createdAtLocal',
          'createdAtUtc',
          'modifiedAtUtc',
          'isPackage',
          'postSale',
          'totalPrice',
          'vertaforeStatus',
          'isContractTransferred'
        )}
        objectName={lang.contract}
      />
      &nbsp;
      <ContractRefundPriceSummariesTable contract={contract} contractRefund={selected} />
      &nbsp;
      <RefundInfoTable
        refundPriceInfo={
          {
            ...selected,
            refundConfigTemplate: selected?.refundConfigTemplateJson ?? selected?.refundConfigTemplate,
          } as Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo
        }
        contract={contract}
        contractCancellation={contractCancellation}
      />
      &nbsp;
      <h1>{lang.vehicle}</h1>
      <div>
        {lang.purchaseState}:{' '}
        <b>
          {translate(vehicle?.purchaseState === PurchaseState.USED ? PurchaseState.PRE_OWNED : vehicle?.purchaseState)}
        </b>
      </div>
      <div>
        {lang.purchaseType}: <b>{translate(vehicle?.purchaseType) ?? 'N/A'}</b>
      </div>
      <div>
        {lang.startingOdometer}:{' '}
        <b>
          <ValueDisplay value={`${vehicle?.posOdometerReading} km`} />
        </b>
      </div>
      <div>
        {lang.maxCoverageKm}:{' '}
        <b>
          <ValueDisplay value={`${contract?.maxCoverageKm ?? 0} km`} />
        </b>
      </div>
      <div>
        {lang.kmsUsed}:{' '}
        <b>
          <ValueDisplay value={`${selected?.usedOdometerReading} km`} />
        </b>
      </div>
      <div>
        {lang.kmConsumed}:{' '}
        <b>
          {formatPercentage(selected?.usedOdometerReadingPercentage)}
        </b>
      </div>
      <div>
        {lang.kmRemaining}:{' '}
        <b>
          {formatPercentage(selected?.remainingOdometerReadingPercentage)}
        </b>
      </div>
      &nbsp; <h3>{lang.termAndExpiryInformation}</h3>
      <div>
        {lang.effectiveDate}: <b>{localizeDate(termStartEffectiveDate, defaultDateTimeFormat)}</b>
      </div>
      <div>
        {lang.expiryDate}: <b>{localizeDate(contract?.expiryDate, defaultDateTimeFormat)}</b>
      </div>
      <div>
        {lang.termInDays}: <b>{selected?.termInDays}</b>
      </div>
      <div>
        {lang.daysConsumed}: <b>{selected?.usedTermInDays}</b>
      </div>
      <div>
        {lang.daysRemaining}: <b>{selected?.remainingTermInDays}</b>
      </div>
      <div>
        {lang.termConsumed}: <b>{formatPercentage(selected?.usedTermInDaysPercentage)}</b>
      </div>
      <div>
        {lang.termRemaining}: <b>{formatPercentage(selected?.remainingTermInDaysPercentage)}</b>
      </div>
      &nbsp;
      <div>
        <h1>{lang.contractPricing}</h1>
        <div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h3>{lang.dealerCost}</h3>
            <div>
              {lang.paymentPrice}:{' '}
              <b>
                <FormatValue value={`$${contract?.paymentPrice}`} />
              </b>
            </div>
            <div>
              {lang.enhacementsPriceCAD}:{' '}
              <b>
                <FormatValue value={`$${contract?.enhancementsPriceCAD}`} />
              </b>
            </div>
            <div>
              {lang.surchargesPriceCAD}:{' '}
              <b>
                <FormatValue value={`$${contract?.surchargesPriceCAD}`} />
              </b>
            </div>
            <div>
              {lang.paymentTaxes}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.dealerCostTaxAmt, 0.01)}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalPriceAmountWPaymentTaxes}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.dealerCostTotal, 0.01)}`} />
              </b>
            </div>
          </div>
          <div>
            <h3>{lang.salePrice}</h3>
            <div>
              {lang.salePrice}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.salePriceCAD, 0.01)}`} />
              </b>
            </div>
            <div>
              {lang.salePriceTaxes}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.totalTaxCAD, 0.01)}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalRetailPriceAmountWSalePriceTaxes}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.salePriceWithTaxCAD, 0.01)}`} />
              </b>
            </div>
          </div>

          <div>
            <h3>{lang.dealerProfitNonASC}</h3>
            <div>
              {lang.dealerProfit}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.dealerProfit, 0.01)}`} />
              </b>
            </div>
            <div>
              {lang.dealerProfitTax}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.dealerProfitTaxAmt, 0.01)}`} />
              </b>
            </div>
            <hr />
            <div>
              {lang.totalDealerProfitWDealerProfitTaxes}:{' '}
              <b>
                <FormatValue value={`$${roundToStep(contract?.dealerProfitWithTaxCAD, 0.01)}`} />
              </b>
            </div>
          </div>
        </div>
      </div>
      &nbsp;
      <h1>{lang.distributions}</h1>
      {!!values(selected?.pricingDistributions).length && (
        <RefundDistributionTable title={lang.pricingDistributions} distributions={selected?.pricingDistributions} />
      )}
      {!!values(selected?.distributorDistributions).length && (
        <RefundDistributionTable
          title={lang.distributorDistributions}
          distributions={selected?.distributorDistributions}
        />
      )}
      &nbsp;
      {selected?.subfees && !isEmpty(selected?.subfees) && (
        <>
          <h1>{lang.surcharges}</h1>
          <RefundSubfeeTable subfees={selected?.subfees} />
        </>
      )}
      {selected?.enhancements && !isEmpty(selected?.enhancements) && (
        <>
          <h1>{lang.enhancements}</h1>

          <DataMap
            data={selected?.enhancements}
            render={({ data: enhancement }) => {
              return (
                <div key={enhancement?.enhancementId}>
                  <h2>{enhancement.name || lang.unknown}:</h2>

                  {!!values(enhancement?.pricingDistributions).length && (
                    <RefundDistributionTable
                      title={lang.pricingDistributions}
                      distributions={enhancement?.pricingDistributions}
                    />
                  )}
                  {!!values(enhancement?.distributorDistributions).length && (
                    <RefundDistributionTable
                      title={lang.distributorDistributions}
                      distributions={enhancement?.distributorDistributions}
                    />
                  )}
                </div>
              );
            }}
          />
        </>
      )}
      &nbsp;
      {!isEmpty(selected?.contractRefundPayees) && (
        <>
          <h1>{lang.payees}</h1>
          {selected?.contractRefundPayees?.map((it, index) => {
            const source = it.clientId ? it.client : it.dealerId ? it.dealer : it.lenderId ? it.lender : it.payee;
            return (
              <ObjectInfo
                key={it.id}
                object={pick(
                  {
                    ...(source || {}),
                    paymentMethodCode: it.dealerId
                      ? (it.paymentMethodCode ?? it.dealer?.paymentMethodCode)
                      : it.paymentMethodCode,
                  },
                  'firstName',
                  'lastName',
                  'name',
                  'email',
                  'phone',
                  'streetAddress',
                  'city',
                  'provinceCode',
                  'postalCode',
                  'taxExempt',
                  'paymentMethodCode'
                )}
                objectName={it.isPrimary ? `${lang.primaryPayee} (${lang.requestor})` : `${lang.payee} #${index}`}
              />
            );
          })}
        </>
      )}
    </>
  );
};

export default SalesContractRefundInfoGeneralInfo;
